/**
 * attachment-url-fixer.ts — Resolves attachment URLs in BlockNote documents.
 *
 * Two responsibilities:
 * 1. Migrates legacy presigned S3 URLs to stable BFF paths (for storage)
 * 2. Resolves stable BFF paths to fresh presigned URLs (for rendering)
 *
 * Storage format: /api/v1/pages/{pageId}/attachments/{attachmentId}/download?redirect=true
 * Render format: Fresh presigned S3 URL (valid for 15 minutes)
 *
 * The document always stores stable paths. On each page load, we resolve
 * these to fresh presigned URLs so <img src="..."> works without auth headers.
 */

import type { PartialBlock } from '@blocknote/core';
import * as pagesApi from '../../api/pages-api';

// ---------------------------------------------------------------------------
// Patterns
// ---------------------------------------------------------------------------

/**
 * Regex matching S3 presigned URLs for page attachments.
 * Matches both virtual-hosted style and path-style S3 URLs.
 */
const S3_ATTACHMENT_URL_REGEX =
  /https?:\/\/[^/]*\.?s3[^/]*\.amazonaws\.com\/(?:[^/]+\/)?pages\/([^/]+)\/attachments\/([^/]+)\/[^?]*/;

/**
 * Regex matching our stable BFF redirect path format.
 * Captures pageId and attachmentId.
 */
const BFF_PATH_REGEX =
  /\/api\/v1\/pages\/([^/]+)\/attachments\/([^/]+)\/download/;

// ---------------------------------------------------------------------------
// Stable path builder
// ---------------------------------------------------------------------------

/**
 * Builds the stable BFF path for a page attachment (stored in document JSON).
 */
export function buildStablePath(pageId: string, attachmentId: string): string {
  return `/api/v1/pages/${encodeURIComponent(pageId)}/attachments/${encodeURIComponent(attachmentId)}/download?redirect=true`;
}

// ---------------------------------------------------------------------------
// Migration: Legacy S3 URLs → Stable BFF paths (for saving)
// ---------------------------------------------------------------------------

/**
 * Checks if a URL is a legacy presigned S3 attachment URL.
 */
function isLegacyS3Url(url: string): boolean {
  return S3_ATTACHMENT_URL_REGEX.test(url);
}

/**
 * Checks if a URL is a stable BFF attachment path.
 */
function isStableBffPath(url: string): boolean {
  return BFF_PATH_REGEX.test(url);
}

/**
 * Converts a legacy S3 presigned URL to a stable BFF path.
 */
function legacyUrlToStablePath(url: string): string | null {
  const match = S3_ATTACHMENT_URL_REGEX.exec(url);
  if (!match) return null;
  const [, pageId, attachmentId] = match;
  return buildStablePath(pageId, attachmentId);
}

/**
 * Extracts pageId and attachmentId from a stable BFF path.
 */
function parseStablePath(url: string): { pageId: string; attachmentId: string } | null {
  const match = BFF_PATH_REGEX.exec(url);
  if (!match) return null;
  return { pageId: decodeURIComponent(match[1]), attachmentId: decodeURIComponent(match[2]) };
}

// ---------------------------------------------------------------------------
// Synchronous: Migrate legacy URLs to stable paths (for storage)
// ---------------------------------------------------------------------------

/**
 * Recursively scans BlockNote document blocks and replaces any legacy
 * presigned S3 URLs with stable BFF paths. Used before saving.
 *
 * Returns a new document array and whether any URLs were fixed.
 */
export function migrateToStablePaths(
  blocks: PartialBlock[]
): { blocks: PartialBlock[]; fixed: boolean } {
  let fixed = false;

  const fixedBlocks = blocks.map((block) => {
    let updatedBlock = block;

    // Check block-level props (image, video, file blocks store url in props)
    const props = (block as any).props;
    if (props?.url && typeof props.url === 'string' && isLegacyS3Url(props.url)) {
      const newUrl = legacyUrlToStablePath(props.url);
      if (newUrl) {
        updatedBlock = { ...updatedBlock, props: { ...props, url: newUrl } };
        fixed = true;
      }
    }

    // Check inline content (links with S3 URLs)
    if (Array.isArray((updatedBlock as any).content)) {
      const content = (updatedBlock as any).content;
      const fixedContent = content.map((item: any) => {
        if (item.type === 'link' && item.href && isLegacyS3Url(item.href)) {
          const newUrl = legacyUrlToStablePath(item.href);
          if (newUrl) {
            fixed = true;
            return { ...item, href: newUrl };
          }
        }
        return item;
      });
      updatedBlock = { ...updatedBlock, content: fixedContent } as any;
    }

    // Recurse into children
    if (Array.isArray((updatedBlock as any).children) && (updatedBlock as any).children.length > 0) {
      const childResult = migrateToStablePaths((updatedBlock as any).children);
      if (childResult.fixed) {
        updatedBlock = { ...updatedBlock, children: childResult.blocks } as any;
        fixed = true;
      }
    }

    return updatedBlock;
  });

  return { blocks: fixedBlocks, fixed };
}

// ---------------------------------------------------------------------------
// Async: Resolve stable paths to fresh presigned URLs (for rendering)
// ---------------------------------------------------------------------------

/**
 * Recursively scans BlockNote document blocks and resolves stable BFF paths
 * and legacy S3 URLs to fresh presigned download URLs.
 *
 * This is called on page load so <img src="..."> gets a working presigned URL.
 * The resolved URLs are for rendering only — they're NOT saved back to the document.
 */
export async function resolveAttachmentUrls(
  blocks: PartialBlock[]
): Promise<{ blocks: PartialBlock[]; hasAttachments: boolean }> {
  // Collect all attachment references that need resolution
  const urlsToResolve: Map<string, { pageId: string; attachmentId: string }> = new Map();

  function collectUrls(blockList: PartialBlock[]): void {
    for (const block of blockList) {
      const props = (block as any).props;
      if (props?.url && typeof props.url === 'string') {
        const parsed = parseStablePath(props.url);
        if (parsed) {
          urlsToResolve.set(props.url, parsed);
        } else if (isLegacyS3Url(props.url)) {
          const stablePath = legacyUrlToStablePath(props.url);
          if (stablePath) {
            const parsedStable = parseStablePath(stablePath);
            if (parsedStable) {
              urlsToResolve.set(props.url, parsedStable);
            }
          }
        }
      }
      if (Array.isArray((block as any).children)) {
        collectUrls((block as any).children);
      }
    }
  }

  collectUrls(blocks);

  if (urlsToResolve.size === 0) {
    return { blocks, hasAttachments: false };
  }

  // Resolve all URLs in parallel
  const resolvedUrls: Map<string, string> = new Map();
  const resolutions = Array.from(urlsToResolve.entries()).map(
    async ([originalUrl, { pageId, attachmentId }]) => {
      try {
        const result = await pagesApi.downloadAttachment(pageId, attachmentId);
        if (result.url) {
          resolvedUrls.set(originalUrl, result.url);
        }
      } catch {
        // If resolution fails, leave the original URL (will show broken image)
      }
    }
  );

  await Promise.all(resolutions);

  // Apply resolved URLs to blocks
  function applyResolutions(blockList: PartialBlock[]): PartialBlock[] {
    return blockList.map((block) => {
      let updatedBlock = block;
      const props = (block as any).props;

      if (props?.url && resolvedUrls.has(props.url)) {
        updatedBlock = {
          ...updatedBlock,
          props: { ...props, url: resolvedUrls.get(props.url) },
        };
      }

      if (Array.isArray((updatedBlock as any).children) && (updatedBlock as any).children.length > 0) {
        updatedBlock = {
          ...updatedBlock,
          children: applyResolutions((updatedBlock as any).children),
        } as any;
      }

      return updatedBlock;
    });
  }

  return { blocks: applyResolutions(blocks), hasAttachments: true };
}

// Keep the old name as an alias for backward compatibility
export { migrateToStablePaths as resolveAttachmentUrls_sync };
